"use client";

import { c as _c } from "react/compiler-runtime";
import { otherTypes } from "@/info/other-types";
import { spaceTypes } from "@/info/spaces";
import formikOptions from "@/utils/formikOptions";
import { groupedOptions } from "@/utils/groupedOptions";
import { logger } from "@/utils/logger";
import { deepEqual as isEqualFunc } from "fast-equals";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { CBResult, FileWithMetadata } from "../dropzone";
import { sanitizeFileName } from "../dropzone/utils";
import { DropzoneInput, FileType } from "../rhf/dropzone-input";
import { ButtonActions } from "./action-buttons";
import { useFormContext } from "./context/FormProvider";
interface UploadsData {
  images: FileWithMetadata[];
}
interface StepUploadsProps {
  onNext: (data: UploadsData) => void;
  onBack: () => void;
  rawUnitId: string | null;
  isLoading?: boolean;
}
export function StepUploads(t0) {
  const $ = _c(46);
  const {
    onNext,
    onBack,
    rawUnitId,
    isLoading
  } = t0;
  const {
    state,
    updateStepData
  } = useFormContext();
  const contextUploads = state.uploads as Partial<UploadsData>;
  let t1;
  if ($[0] !== contextUploads?.images) {
    t1 = contextUploads?.images || [];
    $[0] = contextUploads?.images;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      images: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const defaultValues = t2;
  let t3;
  if ($[4] !== defaultValues) {
    t3 = {
      defaultValues,
      mode: "onChange"
    };
    $[4] = defaultValues;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = useForm(t3);
  let control;
  let handleSubmit;
  let reset;
  let useFormProps;
  if ($[6] !== t4) {
    ({
      control,
      handleSubmit,
      reset,
      ...useFormProps
    } = t4);
    $[6] = t4;
    $[7] = control;
    $[8] = handleSubmit;
    $[9] = reset;
    $[10] = useFormProps;
  } else {
    control = $[7];
    handleSubmit = $[8];
    reset = $[9];
    useFormProps = $[10];
  }
  let t5;
  if ($[11] !== control) {
    t5 = {
      control,
      name: "images"
    };
    $[11] = control;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const watchedImages = useWatch(t5);
  let t6;
  if ($[13] !== watchedImages) {
    t6 = {
      images: watchedImages
    };
    $[13] = watchedImages;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const prevWatched = useRef(t6);
  const updateTimeoutRef = useRef(null);
  let t7;
  let t8;
  if ($[15] !== updateStepData || $[16] !== watchedImages) {
    t7 = () => {
      const currentValues = {
        images: watchedImages
      };
      if (!isEqualFunc(prevWatched.current, currentValues)) {
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        updateTimeoutRef.current = setTimeout(() => {
          updateStepData("uploads", currentValues);
          prevWatched.current = currentValues;
        }, 300);
      }
    };
    t8 = [watchedImages, updateStepData];
    $[15] = updateStepData;
    $[16] = watchedImages;
    $[17] = t7;
    $[18] = t8;
  } else {
    t7 = $[17];
    t8 = $[18];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
    t10 = [];
    $[19] = t10;
    $[20] = t9;
  } else {
    t10 = $[19];
    t9 = $[20];
  }
  useEffect(t9, t10);
  let t11;
  if ($[21] !== onNext) {
    t11 = data => {
      const formattedData = {
        images: data.images?.map(_temp) || []
      };
      onNext(formattedData as UploadsData);
    };
    $[21] = onNext;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const onSubmit = t11;
  const callback = _temp2;
  const tagOptionsFunction = _temp3;
  if (!rawUnitId) {
    let t12;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700">{"After unit creation, upload section can't be edited."}</div>;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    return t12;
  }
  let t12;
  if ($[24] !== control || $[25] !== handleSubmit || $[26] !== reset || $[27] !== useFormProps) {
    t12 = {
      control,
      handleSubmit,
      reset,
      ...useFormProps
    };
    $[24] = control;
    $[25] = handleSubmit;
    $[26] = reset;
    $[27] = useFormProps;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== handleSubmit || $[30] !== onSubmit) {
    t13 = handleSubmit(onSubmit);
    $[29] = handleSubmit;
    $[30] = onSubmit;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== rawUnitId) {
    t14 = <DropzoneInput name="images" label="Upload Images & Files" tagOptions={tagOptionsFunction} uploadDirDoc={{
      collection: "rawUnits",
      _id: rawUnitId
    }} callback={callback} />;
    $[32] = rawUnitId;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== isLoading || $[35] !== onBack) {
    t15 = <ButtonActions next={true} onBack={onBack} isLoading={isLoading} />;
    $[34] = isLoading;
    $[35] = onBack;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t14 || $[38] !== t15) {
    t16 = <div className="space-y-6">{t14}{t15}</div>;
    $[37] = t14;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t13 || $[41] !== t16) {
    t17 = <form onSubmit={t13}>{t16}</form>;
    $[40] = t13;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t12 || $[44] !== t17) {
    t18 = <FormProvider {...t12}>{t17}</FormProvider>;
    $[43] = t12;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  return t18;
}
function _temp3(fileType) {
  if (fileType === "image") {
    const tagOptions = [...spaceTypes, {
      code: "floorPlan",
      extra: true,
      icon: "floorPlan",
      name: "Floor Plan",
      color: "brown",
      furnishingDetails: [],
      grouped: "project"
    }];
    return groupedOptions(tagOptions);
  }
  return formikOptions(otherTypes);
}
function _temp2(res) {
  const {
    name,
    success,
    url,
    error
  } = res as CBResult;
  if (error) {
    toast.error(error.message || "Failed to execute");
    logger.error(`Failed to execute: ${name} - callback`, {
      name,
      success,
      url,
      error
    });
  } else {
    toast.success("Successfully executed");
    logger.info(`Successfully executed: ${name} - callback`, {
      name,
      success,
      url,
      error
    });
  }
}
function _temp(uf) {
  return {
    tags: uf.tags,
    name: sanitizeFileName(uf.name ?? uf.file?.name ?? ""),
    size: uf.size ?? uf.file?.size,
    type: uf.type ?? uf.file?.type,
    url: uf.url
  };
}